import { providers } from 'ethers';
import { useMemo } from 'react';
import { Config, useAccount, useChainId, useConnectorClient } from 'wagmi';
import Web3 from 'web3';
import { AbiItem } from 'web3-utils';

import { getAddress } from '@ethersproject/address';
import { AddressZero } from '@ethersproject/constants';
import { Contract } from '@ethersproject/contracts';
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers';

import { RPC_BASE } from '../constants';

import type { Account, Chain, Client, Transport } from 'viem';

export function clientToSigner(client: Client<Transport, Chain, Account>) {
  const { chain, transport }: any = client;

  if (!chain) {
    return undefined;
  }
  const network = {
    chainId: chain?.id,
    name: chain?.name,
    ensAddress: chain?.contracts?.ensRegistry?.address,
  };
  const provider = new providers.Web3Provider(transport, network);
  //const provider = new ethers.providers.JsonRpcProvider(RPC_BASE);

  return provider;
}

export function isAddress(value: any): string | false {
  try {
    return getAddress(value);
  } catch {
    return false;
  }
}

export function getSigner(
  library: Web3Provider,
  account: string
): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked();
}

export function getProviderOrSigner(
  library: Web3Provider,
  account?: string
): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library;
}

export function getContract(
  address: string,
  ABI: any,
  library: Web3Provider,
  account?: string
): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`);
  }
  return new Contract(
    address,
    ABI,
    getProviderOrSigner(library, account) as any
  );
}

// returns null on errors
export function useContract(
  addresss: any,
  ABI: any,
  withSignerIfPossible = true
) {
  const { address } = useAccount();
  const chainId = useChainId();
  let account = address;
  const { data: client } = useConnectorClient<Config>({ chainId });
  let library: any = client ? clientToSigner(client) : undefined;

  return useMemo(() => {
    if (!addresss || !ABI || !library) return null;
    try {
      return getContract(
        addresss,
        ABI,
        library,
        withSignerIfPossible && account ? account : undefined
      );
    } catch (error) {
      console.error('Failed to get contract', error);
      return null;
    }
  }, [addresss, ABI, library, withSignerIfPossible, account]);
}

/**
 * init contract with Web3 JS
 * @param contractAddress
 * @param ABI
 * @returns
 */
export const useContractWeb3 = (contractAddress: any, ABI: any) => {
  const web3: any = new Web3(RPC_BASE);
  const contract = useMemo(() => {
    if (!contractAddress || !ABI) return null;
    return new web3.eth.Contract(ABI as unknown as AbiItem, contractAddress);
  }, [contractAddress, ABI]);

  return {
    web3,
    contract,
  };
};
